import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { profileQueryKey } from '@/features/profile/useProfile';

import { COURSES_QUERY_KEY } from './useCourses';
import { createCourseEnrollmentService } from './courseEnrollmentService';

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function useAddCourseEnrollment() {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = getKuduUserId(user) ?? user?.uid ?? null;

  const createClient = useCallback(() => {
    if (!user) {
      throw createDataError('You must be signed in to add a course.');
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });
  }, [getIdToken, location.hash, location.pathname, location.search, navigate, user]);

  const lookupCourseForEnrollment = useCallback(
    (courseId: string) =>
      createCourseEnrollmentService(createClient()).lookupCourseForEnrollment(
        courseId,
      ),
    [createClient],
  );

  const startStripeCoursePayment = useCallback(
    (amount: number) =>
      createCourseEnrollmentService(createClient()).startStripeCoursePayment(
        amount,
      ),
    [createClient],
  );

  const addCourseToUser = useCallback(
    async (courseId: string) => {
      if (!userId) {
        throw createDataError('You must be signed in to add a course.');
      }

      await createCourseEnrollmentService(createClient()).addCourseToUser({
        courseId,
        userId,
      });
    },
    [createClient, userId],
  );

  const redeemAccessCode = useCallback(
    async (params: { code: string; courseId: string; valueCents: number }) => {
      if (!userId) {
        throw createDataError('You must be signed in to add a course.');
      }

      await createCourseEnrollmentService(
        createClient(),
      ).redeemCourseAccessCode({
        code: params.code,
        courseId: params.courseId,
        userId,
        valueCents: params.valueCents,
      });
    },
    [createClient, userId],
  );

  const refreshAfterEnrollment = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: COURSES_QUERY_KEY }),
      userId
        ? queryClient.invalidateQueries({ queryKey: profileQueryKey(userId) })
        : Promise.resolve(),
    ]);
  }, [queryClient, userId]);

  return {
    addCourseToUser,
    lookupCourseForEnrollment,
    redeemAccessCode,
    refreshAfterEnrollment,
    startStripeCoursePayment,
    userId,
  };
}
